/* Shopify Ajax cart and predictive search. Server-rendered totals are authoritative. */
(() => {
  'use strict';
  if (window.DFCommerce) return;
  const configuration = window.DFShopify || {};
  const root = (configuration.routes?.root || window.Shopify?.routes?.root || '/').replace(/\/?$/, '/');
  const paths = {
    cart: configuration.routes?.cart || root + 'cart',
    add: configuration.routes?.cartAdd || root + 'cart/add',
    change: configuration.routes?.cartChange || root + 'cart/change',
    predictive: configuration.routes?.predictiveSearch || root + 'search/suggest',
    search: configuration.routes?.search || root + 'search'
  };
  const jsonPath = path => path.replace(/\.js$/, '') + '.js';
  const cartRoots = () => [...document.querySelectorAll('[data-cart-section]')];
  const sectionIds = () => [...new Set(cartRoots().map(node => node.dataset.cartSection))].slice(0, 5);
  const text = key => cartRoots()[0]?.dataset[key] || configuration.strings?.[key] || '';
  const settings = { queue: Promise.resolve(), busy: false, removed: null, trigger: null };
  const parser = new DOMParser();
  let cartRefresh = 0;

  function openCart(trigger) {
    const dialog = document.getElementById('cart-drawer');
    if (!dialog) { window.location.assign(paths.cart); return; }
    if (!dialog.open) {
      settings.trigger = trigger || document.activeElement;
      document.querySelectorAll('dialog[open]').forEach(node => node.close());
      dialog.showModal();
      document.documentElement.classList.add('v4-modal-open');
      dialog.querySelector('[data-cart-close]')?.focus({ preventScroll: true });
    }
  }

  function announce(message, error = false, scope) {
    const nodes = scope ? [scope] : [...document.querySelectorAll('[data-cart-status]')];
    for (const node of nodes) {
      if (!node) continue;
      node.textContent = message;
      node.setAttribute('role', error ? 'alert' : 'status');
    }
  }

  function setBusy(busy) {
    settings.busy = busy;
    for (const node of cartRoots()) {
      node.setAttribute('aria-busy', String(busy));
      for (const control of node.querySelectorAll('[data-cart-action], [data-cart-quantity-input], [data-cart-checkout], [data-cart-undo]')) {
        if (busy) {
          control.dataset.dfWasDisabled = String(control.disabled || control.getAttribute('aria-disabled') === 'true');
          if ('disabled' in control) control.disabled = true;
          control.setAttribute('aria-disabled', 'true');
        } else {
          const wasDisabled = control.dataset.dfWasDisabled === 'true';
          if ('disabled' in control && control.dataset.dfWasDisabled !== undefined) control.disabled = wasDisabled;
          if (!wasDisabled) control.removeAttribute('aria-disabled');
          delete control.dataset.dfWasDisabled;
        }
      }
    }
  }

  async function jsonRequest(url, options = {}) {
    const response = await fetch(url, {
      credentials: 'same-origin',
      headers: { Accept: 'application/json', ...options.headers },
      ...options
    });
    const data = await response.json().catch(() => null);
    if (!response.ok || data?.status >= 400) {
      const detail = typeof data?.description === 'string' ? data.description :
        typeof data?.message === 'string' ? data.message : text('error');
      throw new Error(detail || response.statusText);
    }
    if (!data) throw new Error(text('error'));
    return data;
  }

  function updateUndo() {
    document.querySelectorAll('[data-cart-undo-row]').forEach(node => node.remove());
    if (!settings.removed) return;
    for (const container of document.querySelectorAll('[data-cart-render]')) {
      const row = document.createElement('p');
      row.className = 'cart-undo';
      row.dataset.cartUndoRow = '';
      row.setAttribute('role', 'status');
      const label = document.createElement('span');
      label.textContent = settings.removed.title + ' ' + text('removed') + ' ';
      const undo = document.createElement('button');
      undo.className = 'remove'; undo.type = 'button'; undo.dataset.cartUndo = '';
      undo.textContent = text('undo');
      row.append(label, undo);
      const area = container.querySelector('.cart-products') || container;
      area.prepend(row);
    }
  }

  function applySections(sections) {
    let count = null;
    let rendered = 0;
    for (const current of cartRoots()) {
      const markup = sections?.[current.dataset.cartSection];
      if (typeof markup !== 'string') continue;
      const incoming = parser.parseFromString(markup, 'text/html')
        .querySelector('[data-cart-section="' + CSS.escape(current.dataset.cartSection) + '"]');
      const content = incoming?.querySelector('[data-cart-render]');
      if (!content) continue;
      const oldContent = current.querySelector('[data-cart-render]');
      const scroll = oldContent?.querySelector('.cart-products')?.scrollTop || 0;
      oldContent.innerHTML = content.innerHTML;
      const scroller = oldContent.querySelector('.cart-products');
      if (scroller) scroller.scrollTop = scroll;
      current.dataset.cartItemCount = incoming.dataset.cartItemCount;
      count = Number(incoming.dataset.cartItemCount);
      rendered++;
    }
    if (count !== null) {
      document.querySelectorAll('[data-cart-count]').forEach(node => { node.textContent = count || ''; });
    }
    updateUndo();
    document.dispatchEvent(new CustomEvent('df:cart:updated', { detail: { count } }));
    return rendered;
  }

  async function refreshCart() {
    const ids = sectionIds();
    if (!ids.length) return;
    const version = ++cartRefresh;
    const url = new URL(window.location.href);
    url.searchParams.set('sections', ids.join(','));
    // A storefront page requested as application/json can return cart/product
    // data instead of the requested section markup. Keep HTML page negotiation.
    const sections = await jsonRequest(url.href, { headers: { Accept: '*/*' } });
    if (version === cartRefresh && applySections(sections) < ids.length) throw new Error(text('error'));
  }

  async function refreshAfterMutation(result) {
    cartRefresh++;
    const expected = sectionIds().length;
    const rendered = applySections(result.sections);
    if (rendered < expected) await refreshCart();
  }

  function queue(task, onError) {
    settings.queue = settings.queue.catch(() => {}).then(async () => {
      setBusy(true);
      try { await task(); }
      catch (error) {
        const message = error.message || text('error');
        announce(message, true);
        if (onError) await onError(message);
      } finally { setBusy(false); }
    });
    return settings.queue;
  }

  async function addProduct(form, submitter) {
    if (form.dataset.dfSubmitting === 'true') return;
    form.dataset.dfSubmitting = 'true';
    const data = new FormData(form);
    const errorElement = form.querySelector('[data-product-error], [data-form-error]');
    announce('', false, errorElement);
    const buttons = [...form.querySelectorAll('[type="submit"]')];
    if (submitter && !buttons.includes(submitter)) buttons.push(submitter);
    const buttonState = buttons.map(button => [button, button.disabled]);
    buttons.forEach(button => { button.disabled = true; button.setAttribute('aria-busy', 'true'); });
    await queue(async () => {
      data.set('sections', sectionIds().join(','));
      data.set('sections_url', window.location.pathname);
      const result = await jsonRequest(jsonPath(paths.add), { method: 'POST', body: data });
      settings.removed = null;
      // The add has succeeded even if optional section rendering subsequently fails.
      try { await refreshAfterMutation(result); }
      catch (_) { window.location.assign(paths.cart); return; }
      announce(text('added'));
      openCart(submitter);
      document.dispatchEvent(new CustomEvent('df:product:added', { detail: result }));
    }, async message => {
      // An inventory rejection can still change Shopify's cart; always reconcile once.
      try { await refreshCart(); } catch (_) { /* Keep the original actionable error. */ }
      announce(message, true, errorElement);
    });
    buttonState.forEach(([button, wasDisabled]) => {
      button.disabled = wasDisabled;
      button.removeAttribute('aria-busy');
    });
    delete form.dataset.dfSubmitting;
  }

  function lineNode(key, source) {
    return (source?.isConnected && source.closest('[data-cart-line]')) ||
      document.querySelector('[data-cart-line="' + CSS.escape(key) + '"]');
  }

  function changeQuantity(key, quantity, source, removing = false) {
    if (!key || !Number.isSafeInteger(quantity) || quantity < 0) return;
    const line = lineNode(key, source);
    const action = source?.dataset.cartAction;
    let removedItem;
    if (removing && line) {
      try {
        const item = JSON.parse(line.querySelector('[data-cart-item-json]').textContent);
        removedItem = { title: item.product_title || item.title, item: {
          id: item.variant_id, quantity: item.quantity, properties: item.properties || {}
        }};
        if (item.selling_plan_allocation?.selling_plan?.id) {
          removedItem.item.selling_plan = item.selling_plan_allocation.selling_plan.id;
        }
      } catch (_) { removedItem = null; }
    }
    return queue(async () => {
      const result = await jsonRequest(jsonPath(paths.change), {
        method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: key, quantity, sections: sectionIds(), sections_url: window.location.pathname })
      });
      settings.removed = removing ? removedItem : null;
      try { await refreshAfterMutation(result); }
      catch (_) { window.location.assign(paths.cart); return; }
      announce(text('updated'));
      const control = document.querySelector('[data-cart-action="' + (action || 'increase') + '"][data-line-key="' + CSS.escape(key) + '"]');
      if (control && !control.disabled) control.focus({ preventScroll: true });
      else document.getElementById('cart-drawer')?.querySelector('[data-cart-close]')?.focus({ preventScroll: true });
    }, message => {
      const current = lineNode(key, source);
      announce(message, true, current?.querySelector('[data-line-error]'));
      const input = current?.querySelector('[data-cart-quantity-input]');
      if (input) input.value = current.dataset.cartQuantity;
    });
  }

  function undoRemoval() {
    if (!settings.removed) return;
    const item = settings.removed.item;
    return queue(async () => {
      const result = await jsonRequest(jsonPath(paths.add), {
        method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [item], sections: sectionIds(), sections_url: window.location.pathname })
      });
      settings.removed = null;
      try { await refreshAfterMutation(result); }
      catch (_) { window.location.assign(paths.cart); return; }
      announce(text('updated'));
    });
  }

  document.addEventListener('submit', event => {
    const form = event.target.closest('[data-product-form]');
    if (form && 'fetch' in window) {
      event.preventDefault();
      if (!settings.busy) addProduct(form, event.submitter);
    }
    const cartForm = event.target.closest('[data-cart-form]');
    if (cartForm && settings.busy) event.preventDefault();
  });
  document.addEventListener('click', event => {
    const trigger = event.target.closest('button,a');
    if (!trigger) return;
    if (trigger.matches('[data-cart-open], [data-dialog="cart-drawer"]')) {
      if (!document.getElementById('cart-drawer')) return;
      event.preventDefault();
      openCart(trigger);
      if (!settings.busy) queue(refreshCart);
    } else if (trigger.matches('[data-cart-close]')) {
      document.getElementById('cart-drawer')?.close();
    } else if (trigger.matches('[data-cart-undo]')) {
      event.preventDefault(); if (!settings.busy) undoRemoval();
    } else if (trigger.matches('[data-cart-action]')) {
      event.preventDefault();
      if (settings.busy || trigger.getAttribute('aria-disabled') === 'true') return;
      const line = trigger.closest('[data-cart-line]');
      const input = line.querySelector('[data-cart-quantity-input]');
      const action = trigger.dataset.cartAction;
      let quantity = Number(input.value);
      const increment = Number(input.step) || 1;
      if (action === 'remove') quantity = 0;
      if (action === 'increase') quantity += increment;
      if (action === 'decrease') quantity = Math.max(Number(input.min) || 1, quantity - increment);
      if (input.max) quantity = Math.min(Number(input.max), quantity);
      changeQuantity(trigger.dataset.lineKey, quantity, trigger, action === 'remove');
    }
  });
  document.addEventListener('change', event => {
    const input = event.target.closest('[data-cart-quantity-input]');
    if (!input || settings.busy) return;
    if (!input.checkValidity()) { input.reportValidity(); return; }
    changeQuantity(input.dataset.lineKey, Number(input.value), input);
  });

  function bindCartDialogs() {
    const dialog = document.getElementById('cart-drawer');
    if (!dialog || dialog.dataset.dfBound) return;
    dialog.dataset.dfBound = 'true';
    dialog.addEventListener('close', () => {
      if (!document.querySelector('dialog[open]')) document.documentElement.classList.remove('v4-modal-open');
      if (!document.querySelector('dialog[open]') && settings.trigger?.isConnected) settings.trigger.focus({ preventScroll: true });
    });
    dialog.addEventListener('click', event => {
      if (event.target !== dialog) return;
      const rect = dialog.getBoundingClientRect();
      if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) dialog.close();
    });
  }
  bindCartDialogs();

  const searches = new WeakMap();
  function bindSearch() {
    const input = document.querySelector('[data-search-input]');
    const results = document.querySelector('[data-search-results]');
    if (!input || !results || searches.has(input)) return;
    const state = { timer: null, controller: null, request: 0 };
    searches.set(input, state);
    input.setAttribute('aria-controls', results.id || 'df-search-results');
    if (!results.id) results.id = 'df-search-results';
    async function search() {
      state.controller?.abort();
      const request = ++state.request;
      const term = input.value.trim();
      if (!term) { results.replaceChildren(); results.removeAttribute('aria-busy'); return; }
      state.controller = new AbortController();
      results.setAttribute('aria-busy', 'true');
      try {
        const url = new URL(paths.predictive, window.location.origin);
        url.searchParams.set('q', term);
        url.searchParams.set('section_id', 'df-predictive-search');
        url.searchParams.set('resources[type]', 'product,collection,page,article');
        url.searchParams.set('resources[limit]', '6');
        url.searchParams.set('resources[limit_scope]', 'all');
        url.searchParams.set('resources[options][unavailable_products]', 'last');
        const response = await fetch(url.href, { credentials: 'same-origin', signal: state.controller.signal });
        if (!response.ok) throw new Error();
        const html = await response.text();
        const node = parser.parseFromString(html, 'text/html').querySelector('[data-predictive-content]');
        if (request !== state.request) return;
        results.innerHTML = node?.innerHTML || '';
      } catch (error) {
        if (error.name !== 'AbortError' && request === state.request) {
          results.replaceChildren();
          const message = document.createElement('p');
          message.textContent = results.dataset.error || configuration.strings?.searchError || '';
          const fallback = document.createElement('a');
          fallback.className = 'text-link';
          const url = new URL(paths.search, window.location.origin); url.searchParams.set('q', term);
          fallback.href = url.href;
          fallback.textContent = results.dataset.allResults || configuration.strings?.searchAll || '';
          results.append(message, fallback);
        }
      } finally {
        if (request === state.request) results.removeAttribute('aria-busy');
      }
    }
    input.addEventListener('input', () => { clearTimeout(state.timer); state.request++; state.controller?.abort(); state.timer = setTimeout(search, 180); });
    input.addEventListener('keydown', event => {
      if (event.key === 'ArrowDown') {
        const first = results.querySelector('a');
        if (first) { event.preventDefault(); first.focus(); }
      }
    });
    results.addEventListener('keydown', event => {
      const links = [...results.querySelectorAll('a')];
      const selected = links.indexOf(document.activeElement);
      if (event.key === 'ArrowDown' && links[selected + 1]) { event.preventDefault(); links[selected + 1].focus(); }
      if (event.key === 'ArrowUp') { event.preventDefault(); (links[selected - 1] || input).focus(); }
    });
    // A regular submit deliberately opens Shopify's complete search page.
  }
  bindSearch();
  document.addEventListener('shopify:section:load', () => { bindCartDialogs(); bindSearch(); });
  document.addEventListener('shopify:section:unload', event => {
    const input = event.target.querySelector('[data-search-input]');
    const state = input && searches.get(input);
    if (state) { clearTimeout(state.timer); state.request++; state.controller?.abort(); searches.delete(input); }
  });
  document.addEventListener('shopify:section:select', event => {
    if (event.target.querySelector('#cart-drawer')) openCart();
  });
  document.addEventListener('shopify:block:select', event => {
    if (event.target.closest('[data-cart-section]')?.querySelector('#cart-drawer')) openCart();
  });
  window.addEventListener('pageshow', event => { if (event.persisted && !settings.busy) queue(refreshCart); });
  window.DFCommerce = { refresh: () => queue(refreshCart), open: openCart };
})();

